import { Stack } from "@mui/material";
import { useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import type {
  DraggableFieldType,
  DraggableMetaType,
  FieldWrapper,
  ReportField,
} from "../../../../../../../../api/types/svTypes.ts";
import { AreaItemType, DimensionFieldType, MetaItemType } from "../../../../../../../../api/types/svTypes.ts";
import { selectTileFields } from "../../../../store/selectors/fieldsStateSelectors";
import DropFieldContainer from "../../common/fields/DropFieldContainer";
import FieldOptionPopupContainer from "../../common/fields/measures/FieldOptionPopupContainer";
import MeasureFieldOptions from "../../common/fields/measures/MeasureFieldOptions";
import MetricFieldOptions from "../../common/fields/metrics/MetricFieldOptions";
import useFieldsDropContainer from "../../common/table/hooks/useFieldsDropContainer";
import { getKeyValue } from "../../common/table/tableHelper";
import { createTileField } from "../../common/utilities/createFields";
import DimensionFieldOptions from "./field-options/DimensionFieldOptions";
import OtherOptionsComponent from "./field-options/OtherOptionsComponent";
import { canDropItem } from "./utils/fieldsDropContainerHelper";

const MAX_FIELDS = 1;

interface Props {
  readonlyMode?: boolean;
}

const TileFieldsDropContainer = ({ readonlyMode = false }: Props) => {
  const fieldsAreaValues = useSelector(selectTileFields);
  const {
    createItem,
    handleRemove,
    closePopup,
    handleUpdateConfiguration,
    handleDimensionFieldCustomLabelChange,
    moveItem,
    addItem,
    anchorEl,
    currentField,
    removeItem,
    dimensionFields,
    conditions,
  } = useFieldsDropContainer(fieldsAreaValues, { readonlyMode });

  const fieldsAreaRef = useRef<ReportField[]>(fieldsAreaValues);
  useEffect(() => {
    fieldsAreaRef.current = fieldsAreaValues;
  }, [fieldsAreaValues]);

  const handleCanDropItem = useCallback((item: FieldWrapper<DraggableFieldType> | DraggableMetaType) => {
    if (fieldsAreaRef.current.length >= MAX_FIELDS) {
      return false;
    }
    return canDropItem(item, fieldsAreaRef.current);
  }, []);

  return (
    <Stack sx={{ gap: ".5rem" }}>
      <DropFieldContainer
        title="Table Fields"
        maxFields={MAX_FIELDS}
        areaFieldType={AreaItemType.FIELDS}
        acceptedDropTypes={[
          AreaItemType.DIMENSION,
          AreaItemType.MEASURE,
          AreaItemType.METRIC,
          AreaItemType.CONDITIONS,
          MetaItemType.AGGREGATION,
        ]}
        fields={fieldsAreaValues}
        canDropItem={handleCanDropItem}
        renderListItem={createItem}
        getKeyValue={getKeyValue}
        onAddItem={addItem}
        onRemoveItem={removeItem}
        onMoveItem={moveItem}
        createListItem={createTileField}
        readonly={readonlyMode}
      />
      <FieldOptionPopupContainer anchorEl={anchorEl} cancel={closePopup}>
        {currentField?.type === DimensionFieldType.DIMENSION && (
          <DimensionFieldOptions
            field={currentField}
            cancel={closePopup}
            onRemove={handleRemove}
            onUpdateConfiguration={handleUpdateConfiguration}
            onUpdateCustomLabel={handleDimensionFieldCustomLabelChange}
            readonly={readonlyMode}
          />
        )}
        {currentField?.type === DimensionFieldType.MEASURE && (
          <MeasureFieldOptions
            field={currentField}
            dimensionFields={dimensionFields}
            conditions={conditions}
            otherOptions={
              <OtherOptionsComponent
                measure={currentField}
                saveChanges={handleUpdateConfiguration}
                readonly={readonlyMode}
              />
            }
            cancel={closePopup}
            saveChanges={handleUpdateConfiguration}
            onRemove={handleRemove}
            canAddCustomField={false}
            onCalculationItemClick={undefined}
            readonly={readonlyMode}
          />
        )}
        {currentField?.type === DimensionFieldType.METRIC && (
          <MetricFieldOptions
            field={currentField}
            otherOptions={
              <OtherOptionsComponent
                measure={currentField}
                saveChanges={handleUpdateConfiguration}
                readonly={readonlyMode}
              />
            }
            cancel={closePopup}
            saveChanges={handleUpdateConfiguration}
            onRemove={handleRemove}
            readonly={readonlyMode}
          />
        )}
      </FieldOptionPopupContainer>
    </Stack>
  );
};

export default TileFieldsDropContainer;
